import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

function Editor({ item, type, onClose, onSaved }) {
  const [f, setF] = useState(item || { name: "", slug: "", description: "", image: "", status: "active", seo: { title: "", description: "" }, bottom_content: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (item?.id) await api.put(`/admin/${type}/${item.id}`, f);
      else await api.post(`/admin/${type}`, f);
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid={`${type}-editor`}>
      <div className="bg-[#FAF8F5] max-w-xl mx-auto lg:my-10 min-h-screen lg:min-h-0 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl">{item?.id ? "Edit" : "New"} {type === "categories" ? "Category" : "Collection"}</h2>
          <button onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div><label className="tq-label">Name *</label>
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cat-name" className="tq-input" /></div>
          <div><label className="tq-label">Slug</label>
            <input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="auto from name" className="tq-input" /></div>
          <div><label className="tq-label">Description / SEO Intro</label>
            <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="tq-input" /></div>
          <div><label className="tq-label">Image URL</label>
            <input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} data-testid="cat-image" className="tq-input" /></div>
          {f.image && <img src={f.image} alt="" className="w-24 h-28 object-cover border border-[#E5DEC9]" />}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tq-label">SEO Title</label>
              <input value={f.seo?.title || ""} onChange={(e) => setF({ ...f, seo: { ...f.seo, title: e.target.value } })} className="tq-input" /></div>
            <div><label className="tq-label">Status</label>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="tq-input bg-white">
                <option value="active">Active</option><option value="hidden">Hidden</option>
              </select></div>
          </div>
          <div><label className="tq-label">Meta Description</label>
            <textarea rows={2} value={f.seo?.description || ""} onChange={(e) => setF({ ...f, seo: { ...f.seo, description: e.target.value } })} className="tq-input" /></div>
          {type === "categories" && (
            <div><label className="tq-label">Bottom SEO Content (HTML)</label>
              <textarea rows={3} value={f.bottom_content || ""} onChange={(e) => setF({ ...f, bottom_content: e.target.value })} className="tq-input" /></div>
          )}
          <button type="submit" disabled={busy} data-testid="cat-save" className="tq-btn-dark w-full">{busy ? "Saving…" : "Save"}</button>
        </form>
      </div>
    </div>
  );
}

export function AdminCategories() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/categories").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this category?")) return; await api.delete(`/admin/categories/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Categories</h1>
        <button onClick={() => setEditing({})} data-testid="category-create-btn" className="tq-btn-dark !py-2.5"><Plus size={14} /> New Category</button>
      </div>
      {!items ? <div className="h-48 bg-white animate-pulse" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-[#E5DEC9] p-4 flex gap-4" data-testid={`category-row-${c.slug}`}>
              {c.image && <img src={c.image} alt="" className="w-14 h-16 object-cover bg-[#F4EFE6]" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium">{c.name}</p>
                <p className="text-xs text-[#9a938a]">/collections/{c.slug}</p>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => setEditing(c)} data-testid={`category-edit-${c.slug}`} className="text-xs text-[#C5A059] font-semibold flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  <button onClick={() => del(c.id)} data-testid={`category-delete-${c.slug}`} className="text-xs text-[#8B1E21] font-semibold flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <Editor item={editing.id ? editing : null} type="categories" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

export function AdminCollections() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/collections").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this collection?")) return; await api.delete(`/admin/collections/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="admin-collections">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Collections</h1>
        <button onClick={() => setEditing({})} data-testid="collection-create-btn" className="tq-btn-dark !py-2.5"><Plus size={14} /> New Collection</button>
      </div>
      {!items ? <div className="h-48 bg-white animate-pulse" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-[#E5DEC9] p-4 flex gap-4" data-testid={`collection-row-${c.slug}`}>
              {c.image && <img src={c.image} alt="" className="w-14 h-16 object-cover bg-[#F4EFE6]" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium">{c.name}</p>
                <p className="text-xs text-[#9a938a]">/collections/{c.slug}</p>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => setEditing(c)} data-testid={`collection-edit-${c.slug}`} className="text-xs text-[#C5A059] font-semibold flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  <button onClick={() => del(c.id)} className="text-xs text-[#8B1E21] font-semibold flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <Editor item={editing.id ? editing : null} type="collections" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
